import { DateTime } from 'luxon';
import { action, makeObservable, observable, runInAction, observableRef, observableShallow } from 'mobx';
import { v4 } from 'uuid';

import type {
  AgencyPreviewSession,
  GetMyAgencyOperatorAccessQuery,
} from '@clipr/lib';

import type { Store } from '../../store/store';
import { StoreNode } from '../../store/storeNode';

export type AgencyOperatorProfile = GetMyAgencyOperatorAccessQuery['getMyAgencyOperatorAccess'];

export const agencyOperatorStatusLabel = (status: string, expiringSoon: boolean) =>
  expiringSoon && status === 'ACTIVE'
    ? 'Expiring soon'
    : `${status.charAt(0)}${status.slice(1).toLowerCase()}`;

const days = (count: number) => `${count} ${count === 1 ? 'day' : 'days'}`;

/** Renders the server's calendar-day count, so every surface agrees with the banner tiers. */
export const agencyOperatorDaysRemainingLabel = (daysRemaining: number) => daysRemaining === 0
  ? 'today'
  : daysRemaining > 0 ? `in ${days(daysRemaining)}` : `${days(-daysRemaining)} ago`;

export type AgencyOperatorWarning = {
  agencyId: string;
  agencyName: string;
  daysRemaining: number;
  expiresAt: string;
  grantId: string;
  tier: 1 | 2 | 3;
  timezone: string;
  affectedCount: number;
};

export function selectAgencyOperatorWarning(
  profile: AgencyOperatorProfile | null,
  now = new Date(),
): AgencyOperatorWarning | undefined {
  if (profile?.warning && Date.parse(profile.warning.expiresAt) > now.getTime())
    return profile.warning as AgencyOperatorWarning;
  const warnings = (profile?.access ?? [])
    .filter(({ effectiveStatus }) => effectiveStatus === 'ACTIVE')
    .flatMap(({ agency, grant, daysRemaining }) => {
      const timezone = agency.timezone ?? '';
      if (!timezone || Date.parse(grant.expiresAt) <= now.getTime()) return [];
      const tier = daysRemaining >= 15 && daysRemaining <= 30 ? 1
        : daysRemaining >= 4 && daysRemaining <= 14 ? 2
          : daysRemaining >= 0 && daysRemaining <= 3 ? 3
            : undefined;
      return tier ? [{
        agencyId: agency.id,
        agencyName: agency.displayName || agency.name,
        daysRemaining,
        expiresAt: grant.expiresAt,
        grantId: grant.grantId,
        tier,
        affectedCount: 0,
        timezone,
      } satisfies AgencyOperatorWarning] : [];
    })
    .sort((a, b) => b.tier - a.tier || a.daysRemaining - b.daysRemaining);
  if (!warnings?.length) return undefined;
  return { ...warnings[0], affectedCount: warnings.length };
}

const PREVIEW_STORAGE_KEY = 'agencyPreviewSession';

type StoredPreview = {
  userId: string;
  agencyId: string;
  sessionId: string;
  agencyName: string;
  expiresAt: string;
};

export class AgencyOperatorAccessState extends StoreNode {
  readonly nodeType = 'AgencyOperatorAccessState';

  constructor(
    store: Store,
    private readonly now: () => Date = () => new Date(),
    private readonly createSessionId: () => string = v4,
  ) {
    super(store);
    makeObservable<this, 'sessionDismissed'>(this, {
      profile: observableRef,
      previewSession: observableRef,
      previewAgencyName: observable,
      isLoading: observable,
      isPreviewChanging: observable,
      error: observable,
      isDailyInterstitialOpen: observable,
      scopeVersion: observable,
      certificateDownloadGrantId: observable,
      pendingRenewalAgencyIds: observableShallow,
      sessionDismissed: observableShallow,
      syncIdentity: action,
      routeChanged: action,
      dismissWarning: action,
      openDailyInterstitialIfDue: action,
      closeDailyInterstitial: action,
      beginPreview: action,
      endPreview: action,
      requestRenewal: action,
      downloadCertificate: action,
    });
  }

  profile: AgencyOperatorProfile | null = null;
  previewSession: AgencyPreviewSession | null = null;
  previewAgencyName: string | null = null;
  isLoading = false;
  isPreviewChanging = false;
  error: string | null = null;
  isDailyInterstitialOpen = false;
  scopeVersion = 0;
  certificateDownloadGrantId: string | null = null;
  pendingRenewalAgencyIds = new Set<string>();
  private loadedUserId: string | null = null;
  private loading: Promise<void> | null = null;
  private previewUserId: string | null = null;
  private readonly sessionDismissed = new Set<string>();
  private identityUserId: string | null = null;
  private previewRequestSeq = 0;
  private certificateDownloadSeq = 0;
  private pendingPreviewAttempt: { agencyId: string; sessionId: string } | null = null;
  private accessRefresh: Promise<void> | null = null;
  private restoreCheckedUserId: string | null = null;
  private restoring: Promise<void> | null = null;

  /** The backend refuses team analytics to anyone holding an active grant. */
  get hasActiveGrant(): boolean {
    return this.profile?.access.some(({ effectiveStatus }) => effectiveStatus === 'ACTIVE') ?? false;
  }

  get warning(): AgencyOperatorWarning | undefined {
    return selectAgencyOperatorWarning(this.profile, this.now());
  }

  get visibleWarning(): AgencyOperatorWarning | undefined {
    const warning = this.warning;
    if (!warning) return undefined;
    if (warning.tier === 1 && this.sessionDismissed.has(this.warningKey(warning))) return undefined;
    return warning;
  }

  get activePreviewAgencyId(): string | null {
    return this.previewUserId === this.store.user?.id
      && (this.store.user?.roles.includes('Super Admin') ?? false)
      && this.previewSession?.status === 'ACTIVE'
      && Date.parse(String(this.previewSession.expiresAt)) > this.now().getTime()
      ? this.previewSession.agencyId
      : null;
  }

  headersForOperation(operationName: string | null): Record<string, string> | undefined {
    const agencyId = this.activePreviewAgencyId;
    return agencyId && operationName !== 'beginAgencyPreview' && operationName !== 'endAgencyPreview'
      ? { 'x-agency-preview-id': agencyId }
      : undefined;
  }

  /**
   * Restores a preview persisted before a page reload. Every request except begin/end waits on
   * it, so no agency-scoped fetch goes out before the preview header is known.
   */
  ensurePreviewRestored(operationName: string | null): Promise<void> | null {
    const userId = this.store.user?.id;
    if (!userId || operationName === 'beginAgencyPreview' || operationName === 'endAgencyPreview') return null;
    if (this.restoreCheckedUserId !== userId) {
      this.restoreCheckedUserId = userId;
      this.restoring = this.restorePreview(userId);
    }
    return this.restoring;
  }

  private restorePreview(userId: string): Promise<void> | null {
    const stored = this.readStoredPreview();
    if (!stored) return null;
    if (stored.userId !== userId || Date.parse(stored.expiresAt) <= this.now().getTime()
      || !this.store.user?.roles.includes('Super Admin')) {
      this.store.storage.removeLocal(PREVIEW_STORAGE_KEY);
      return null;
    }
    this.syncIdentity(userId);
    const requestSeq = ++this.previewRequestSeq;
    // Re-sending begin with the same id is idempotent: the server returns the session as it is.
    return this.store.apiService.beginAgencyPreview(
      { agencyId: stored.agencyId, sessionId: stored.sessionId }, { headers: {} },
    ).then(([result]) => runInAction(() => {
      this.restoring = null;
      if (requestSeq !== this.previewRequestSeq || userId !== (this.store.user?.id ?? null)) return;
      const session = result?.beginAgencyPreview;
      if (session?.status !== 'ACTIVE' || session.sessionId !== stored.sessionId
        || session.agencyId !== stored.agencyId) {
        this.store.storage.removeLocal(PREVIEW_STORAGE_KEY);
        return;
      }
      this.previewSession = session;
      this.previewAgencyName = stored.agencyName;
      this.previewUserId = userId;
      this.scopeVersion++;
      this.store.resetPreviewScopedState();
    })).catch(() => runInAction(() => {
      // A failed restore must not hold every later request hostage; drop the saved preview instead.
      this.restoring = null;
      this.store.storage.removeLocal(PREVIEW_STORAGE_KEY);
    }));
  }

  private readStoredPreview(): StoredPreview | null {
    try {
      const stored = JSON.parse(this.store.storage.getLocal(PREVIEW_STORAGE_KEY) ?? 'null') as StoredPreview | null;
      return stored && typeof stored.userId === 'string' && typeof stored.agencyId === 'string'
        && typeof stored.sessionId === 'string' && typeof stored.expiresAt === 'string' ? stored : null;
    } catch {
      return null;
    }
  }

  syncIdentity(userId: string | null) {
    if (this.identityUserId === userId) return;
    if (this.identityUserId !== null) this.store.storage.removeLocal(PREVIEW_STORAGE_KEY);
    this.identityUserId = userId;
    this.loadedUserId = null;
    this.loading = null;
    this.profile = null;
    this.previewSession = null;
    this.previewAgencyName = null;
    this.previewUserId = null;
    this.pendingRenewalAgencyIds.clear();
    this.sessionDismissed.clear();
    this.isDailyInterstitialOpen = false;
    this.isLoading = false;
    this.isPreviewChanging = false;
    this.certificateDownloadGrantId = null;
    this.certificateDownloadSeq++;
    this.error = null;
    this.pendingPreviewAttempt = null;
    this.previewRequestSeq++;
    this.scopeVersion++;
  }

  async load(userId: string): Promise<void> {
    this.syncIdentity(userId);
    if (this.loadedUserId === userId && this.loading) return this.loading;
    this.loadedUserId = userId;
    this.isLoading = true;
    this.error = null;
    const load = (async () => {
      const access = new Map<string, AgencyOperatorProfile['access'][number]>();
      const history = new Map<string, AgencyOperatorProfile['history'][number]>();
      let accessAfter: string | undefined;
      let historyAfter: string | undefined;
      let profile: AgencyOperatorProfile | null = null;
      let errorMessage: string | null = null;
      const seenCursors = new Set<string>();
      while (true) {
        const [result, error] = await this.store.apiService.getMyAgencyOperatorAccess({
          accessFirst: 100,
          ...(accessAfter && { accessAfter }),
          historyFirst: 100,
          ...(historyAfter && { historyAfter }),
        });
        if (this.loadedUserId !== userId) return;
        const next = result?.getMyAgencyOperatorAccess;
        if (error || !next) {
          errorMessage = error?.message ?? 'Agency operator access could not be loaded.';
          break;
        }
        profile = next;
        next.access.forEach(item => access.set(item.grant.grantId, item));
        next.history.forEach(item => history.set(`${item.grantId}:${item.occurredAt}`, item));
        if (!next.accessPageInfo.hasNextPage && !next.historyPageInfo.hasNextPage) {
          break;
        }
        if (next.accessPageInfo.hasNextPage) accessAfter = next.accessPageInfo.endCursor ?? undefined;
        if (next.historyPageInfo.hasNextPage) historyAfter = next.historyPageInfo.endCursor ?? undefined;
        if ((next.accessPageInfo.hasNextPage && !accessAfter)
          || (next.historyPageInfo.hasNextPage && !historyAfter)) {
          errorMessage = 'Agency operator access pagination failed.';
          break;
        }
        const cursorKey = `${accessAfter ?? ''}\0${historyAfter ?? ''}`;
        if (seenCursors.has(cursorKey)) {
          errorMessage = 'Agency operator access pagination failed.';
          break;
        }
        seenCursors.add(cursorKey);
      }
      runInAction(() => {
        if (this.loadedUserId !== userId) return;
        this.profile = profile ? { ...profile, access: [...access.values()], history: [...history.values()] } : null;
        this.pendingRenewalAgencyIds.clear();
        profile?.pendingRenewals?.forEach(request => {
          if (request.status === 'PENDING') this.pendingRenewalAgencyIds.add(request.agencyId);
        });
        this.error = errorMessage;
        this.isLoading = false;
        this.loading = null;
      });
    })();
    this.loading = load;
    return load;
  }

  async refreshAfterRequest(operation: string | null): Promise<void> {
    const user = this.store.user;
    if (!operation || !user || this.activePreviewAgencyId
      || ['getMyAgencyOperatorAccess', 'beginAgencyPreview', 'endAgencyPreview'].includes(operation)) return;
    await this.load(user.id);
    this.openDailyInterstitialIfDue();
  }

  /**
   * A revoked or expired grant leaves the loaded profile stale. Reload the profile and its
   * permissions and the sidebar libraries, so agency links drop off on the next render.
   */
  handleAccessRefused(): Promise<void> {
    this.accessRefresh ??= (async () => {
      await this.store.authService.runRefreshContextFlow();
      await this.store.pageSidebar.fetchTeams();
    })().finally(() => { this.accessRefresh = null; });
    return this.accessRefresh;
  }

  routeChanged() {
    this.openDailyInterstitialIfDue();
  }

  dismissWarning() {
    const warning = this.warning;
    if (warning?.tier === 1) this.sessionDismissed.add(this.warningKey(warning));
  }

  openDailyInterstitialIfDue() {
    const warning = this.warning;
    const userId = this.store.user?.id;
    if (!userId || !warning || warning.tier !== 3) return;
    const date = DateTime.fromJSDate(this.now(), { zone: warning.timezone }).toISODate();
    if (!date) return;
    const key = `agencyOperatorExpiryInterstitial:${userId}`;
    if (this.store.storage.getLocal(key) === date) return;
    this.store.storage.setLocal(key, date);
    this.isDailyInterstitialOpen = true;
  }

  closeDailyInterstitial() { this.isDailyInterstitialOpen = false; }

  async beginPreview(agencyId: string, agencyName: string): Promise<void> {
    const requestSeq = ++this.previewRequestSeq;
    const userId = this.store.user?.id ?? null;
    const sessionId = this.pendingPreviewAttempt?.agencyId === agencyId
      ? this.pendingPreviewAttempt.sessionId
      : this.createSessionId();
    this.pendingPreviewAttempt = { agencyId, sessionId };
    this.isPreviewChanging = true;
    this.error = null;
    const [result, error] = await this.store.apiService.beginAgencyPreview(
      { agencyId, sessionId }, { headers: {} },
    );
    runInAction(() => {
      if (requestSeq !== this.previewRequestSeq || userId !== (this.store.user?.id ?? null)) return;
      this.isPreviewChanging = false;
      this.error = error?.message ?? null;
      const session = result?.beginAgencyPreview;
      // A replayed id can come back ENDED; never reuse it.
      if (session) this.pendingPreviewAttempt = null;
      if (session?.status === 'ACTIVE') {
        this.previewSession = session;
        this.previewAgencyName = agencyName;
        this.previewUserId = userId;
        if (userId) {
          this.store.storage.setLocal(PREVIEW_STORAGE_KEY, JSON.stringify({
            userId, agencyId: session.agencyId, sessionId: session.sessionId,
            agencyName, expiresAt: String(session.expiresAt),
          } satisfies StoredPreview));
        }
        this.scopeVersion++;
        this.store.resetPreviewScopedState();
      }
    });
  }

  async endPreview(): Promise<void> {
    const agencyId = this.previewSession?.agencyId;
    const sessionId = this.previewSession?.sessionId;
    if (!agencyId || !sessionId) return;
    const requestSeq = ++this.previewRequestSeq;
    const userId = this.store.user?.id ?? null;
    this.isPreviewChanging = true;
    this.error = null;
    const [result, error] = await this.store.apiService.endAgencyPreview(
      { agencyId, sessionId }, { headers: {} },
    );
    const ended = runInAction(() => {
      if (requestSeq !== this.previewRequestSeq || userId !== (this.store.user?.id ?? null)) return false;
      this.isPreviewChanging = false;
      this.error = error?.message ?? null;
      if (result?.endAgencyPreview.status === 'ENDED'
        && result.endAgencyPreview.sessionId === sessionId) {
        this.store.storage.removeLocal(PREVIEW_STORAGE_KEY);
        this.previewSession = null;
        this.pendingPreviewAttempt = null;
        this.previewAgencyName = null;
        this.previewUserId = null;
        this.scopeVersion++;
        this.store.resetPreviewScopedState();
        return true;
      }
      return false;
    });
    if (ended) await this.store.userDashboardPage.mounted({});
  }

  async requestRenewal(agencyId: string): Promise<void> {
    const scopeVersion = this.scopeVersion;
    const [result, error] = await this.store.apiService.requestAgencyOperatorRenewal({ agencyId });
    runInAction(() => {
      if (scopeVersion !== this.scopeVersion) return;
      this.error = error?.message ?? null;
      if (result?.requestAgencyOperatorRenewal) this.pendingRenewalAgencyIds.add(agencyId);
    });
  }

  isRenewalPending(agencyId: string): boolean {
    return this.pendingRenewalAgencyIds.has(agencyId);
  }

  isCertificateDownloadPending(grantId: string): boolean {
    return this.certificateDownloadGrantId === grantId;
  }

  async downloadCertificate(agencyId: string, userId: string, grantId: string, version: string): Promise<void> {
    if (this.certificateDownloadGrantId) return;
    const requestSeq = ++this.certificateDownloadSeq;
    const scopeVersion = this.scopeVersion;
    const identityUserId = this.store.user?.id ?? null;
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    this.certificateDownloadGrantId = grantId;
    this.error = null;
    const [result, error] = await this.store.apiService.agencyOperatorCertificateDownloadUrl({
      agencyId, userId, grantId, version,
    });
    const url = result?.agencyOperatorCertificateDownloadUrl;
    if (requestSeq !== this.certificateDownloadSeq || scopeVersion !== this.scopeVersion
      || identityUserId !== (this.store.user?.id ?? null)) {
      tab?.close();
      return;
    }
    runInAction(() => {
      this.certificateDownloadGrantId = null;
      this.error = error?.message ?? (url ? null : 'Certificate download URL was not returned.');
    });
    if (!url || error) {
      tab?.close();
      return;
    }
    if (tab) tab.location.href = url;
    else window.location.assign(url);
  }

  private warningKey(warning: AgencyOperatorWarning): string {
    return `${warning.grantId}:${warning.expiresAt}`;
  }
}
